import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  metadata: Record<string, unknown>;
}

export function AuditMetadataCell({ metadata }: Props): JSX.Element {
  const { t } = useTranslation('adminAudit');
  const [open, setOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const entries = Object.entries(metadata ?? {});

  if (entries.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('metadata.empty')}</span>;
  }

  const summary = entries
    .slice(0, 2)
    .map(([k, v]) => `${k}=${formatValue(v)}`)
    .join(', ');

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-start text-xs text-muted-foreground underline-offset-2 hover:underline"
        aria-expanded={open}
      >
        {open ? t('metadata.hide') : `${summary}${entries.length > 2 ? '…' : ''}`}
      </button>
      {open ? (
        <div className="space-y-2 rounded border border-border bg-muted/40 p-2">
          {showRaw ? (
            <pre className="overflow-x-auto whitespace-pre-wrap text-xs font-mono">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          ) : (
            <dl className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1 text-xs">
              {entries.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="font-medium text-muted-foreground">{k}</dt>
                  <dd className="break-all font-mono">{formatValue(v)}</dd>
                </div>
              ))}
            </dl>
          )}
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          >
            {t('metadata.raw')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}
